/**
 * Trajet en cours — brouillon côté serveur.
 *
 * Le pointage pose sa ligne en base au clic. Le trajet fait désormais pareil :
 * dès « Démarrer », une ligne est créée dans `trip_sessions`, rafraîchie au
 * fil du parcours, effacée à l'arrêt.
 *
 * Ce n'est pas encore une ligne de `trips` — 0 km et pas de motif ne
 * passeraient pas ses contraintes, et on ne desserre pas les garde-fous de la
 * table qui alimente la paie pour la commodité d'un écran. C'est une trace de
 * reprise : un conducteur qui perd son téléphone retrouve son trajet en se
 * reconnectant ailleurs.
 *
 * Aucun appel d'ici ne doit faire échouer le suivi GPS. Un serveur
 * injoignable au milieu d'une course ne doit pas arrêter l'enregistrement :
 * les erreurs sont avalées, le trajet continue en local.
 */
import { rpc } from '@/lib/http';

export interface TripSession {
  driver_id: string;
  company_id: string | null;
  vehicle_id: string | null;
  kind: 'professionnel' | 'prive';
  reason: string | null;
  start_address: string | null;
  start_lat: number | null;
  start_lng: number | null;
  last_lat: number | null;
  last_lng: number | null;
  distance_km: number;
  max_speed_kmh: number | null;
  started_at: string;
  heartbeat_at: string;
}

/** Les échecs réseau ne remontent jamais : le trajet prime sur la synchro. */
async function quiet<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch {
    return null;
  }
}

export const tripSessionService = {
  /** Le trajet en cours côté serveur, s'il y en a un. */
  current: () => quiet(rpc<TripSession | null>('trip.current')),

  start: (p: {
    kind: 'professionnel' | 'prive';
    lat?: number;
    lng?: number;
    start_address?: string;
    reason?: string;
    vehicle_id?: string;
  }) => quiet(rpc<TripSession>('trip.start', { ...p })),

  ping: (p: {
    distance_km: number;
    lat?: number;
    lng?: number;
    speed_kmh?: number;
    start_address?: string;
  }) => quiet(rpc<{ ok: true }>('trip.ping', { ...p })),

  end: (cancelled = false) => quiet(rpc<{ ok: true }>('trip.end', { cancelled })),
};
